import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import VerifyDialog from "@/components/VerifyDialog";
import WhatsAppFab from "@/components/WhatsAppFab";
import GymGearFab from "@/components/GymGearFab";
import ReviewsSection from "@/components/ReviewsSection";
import Logo from "@/components/Logo";
import { CATEGORIES } from "@/data/products";
import { useCartCount } from "@/hooks/useCart";
import { useSettings } from "@/hooks/useSettings";
import { useCategoryImages, resolveCategoryImage } from "@/hooks/useCategoryImages";
import { useT } from "@/i18n/LanguageContext";
import {
  Shield, FlaskConical, Snowflake,
  Package, Globe, AlertTriangle, Headphones,
  ArrowRight, Search,
  ShoppingBag, User, BadgeCheck, ChevronRight,
  Mail, Send, Instagram,
} from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/f89a09f9-8a15-42a4-bac3-14002f59cee6/images/0408dbd304d611365ba71133746254e35049ebd1d2cfaaf9e91cbd33485f072f.png";
const PREMIUM_BOX = "/brand/hero_bottle.jpg";
const HERO_PEN = "/brand/hero_pen_sprays.jpg";

const HERO_FEATURES = [
  { icon: FlaskConical, titleKey: "hero.feat.hplc", subKey: "hero.feat.hplc_sub" },
  { icon: Shield, titleKey: "hero.feat.third", subKey: "hero.feat.third_sub" },
  { icon: Snowflake, titleKey: "hero.feat.cold", subKey: "hero.feat.cold_sub" },
  { icon: BadgeCheck, titleKey: "hero.feat.anti", subKey: "hero.feat.anti_sub" },
];

const SHIPPING_FEATURES = [
  { icon: Package, titleKey: "ship.discreet", descKey: "ship.discreet_sub" },
  { icon: Globe, titleKey: "ship.worldwide", descKey: "ship.worldwide_sub" },
  { icon: AlertTriangle, titleKey: "ship.research_only", descKey: "ship.research_only_sub" },
  { icon: Headphones, titleKey: "ship.support", descKey: "ship.support_sub" },
];

const STATS = [
  { v: "99%+", labelKey: "stats.purity" },
  { v: "500+", labelKey: "stats.researchers" },
  { v: "50+", labelKey: "stats.peptides" },
  { v: "100%", labelKey: "stats.research_grade" },
];

export default function Home() {
  const navigate = useNavigate();
  const t = useT();
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const goToProducts = (q) => {
    const params = (q ?? searchQuery).trim() ? `?q=${encodeURIComponent((q ?? searchQuery).trim())}` : "";
    navigate(`/products${params}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    goToProducts();
  };
  const cartCount = useCartCount();
  const settings = useSettings();
  const categoryImages = useCategoryImages();
  const heroPenSrc = resolveCategoryImage("pens", categoryImages) !== "/brand/categories/pens.png"
    ? resolveCategoryImage("pens", categoryImages)
    : HERO_PEN;
  const waSupreme = (settings.whatsapp_supreme || "").replace(/\D/g, "");
  const waDisplay = waSupreme ? `+${waSupreme}` : "";

  return (
    <div className="min-h-screen bg-[#020409] text-white">
      {/* TOP UTILITY STRIP */}
      <div className="border-b border-white/5 bg-black/60 backdrop-blur-md text-[11px] uppercase tracking-[0.2em] font-medium">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-zinc-500">
            <ChevronRight className="w-3 h-3 text-[#39FF14]" />
            <span className="hidden sm:inline">{t("util.tagline")}</span>
            <span className="sm:hidden">{t("util.tagline.short")}</span>
            <span className="hidden md:inline-flex items-center gap-1.5 ml-3 pl-3 border-l border-white/10 text-[10px] text-red-400 font-bold tracking-[0.18em] uppercase">
              <AlertTriangle className="w-3 h-3" />
              {t("util.research_only")}
            </span>
          </div>
          <div className="hidden md:flex items-center gap-5 text-zinc-400">
            <button onClick={() => setVerifyOpen(true)} className="flex items-center gap-1.5 hover:text-[#39FF14] transition" data-testid="util-verify">
              <Shield className="w-3.5 h-3.5 text-[#39FF14]" /> {t("util.verify_product")}
            </button>
            <a href="/admin" className="flex items-center gap-1.5 hover:text-white transition" data-testid="util-account">
              <User className="w-3.5 h-3.5" /> {t("util.my_account")}
            </a>
            <button onClick={() => navigate("/cart")} className="flex items-center gap-1.5 hover:text-[#39FF14] transition relative" data-testid="util-cart">
              <ShoppingBag className="w-3.5 h-3.5" /> {t("util.cart")}
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-3 w-4 h-4 rounded-full bg-[#39FF14] text-black text-[9px] font-bold flex items-center justify-center">{cartCount}</span>
              )}
            </button>
          </div>
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => navigate("/cart")}
              data-testid="util-cart-mobile"
              className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-[#39FF14] transition relative px-2.5 py-1 rounded-md border border-white/10 bg-white/5"
            >
              <ShoppingBag className="w-3.5 h-3.5 text-[#39FF14]" />
              <span className="text-[11px] uppercase tracking-wider font-bold">{t("util.cart")}</span>
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#39FF14] text-black text-[9px] font-bold flex items-center justify-center">{cartCount}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* MAIN HEADER */}
      <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-[#020409]/85 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-8">
          <a href="#home" data-testid="logo"><Logo /></a>
          <nav className="hidden lg:flex items-center gap-9 text-xs font-bold uppercase tracking-[0.18em] text-zinc-300">
            <a href="#home" className="text-[#39FF14] relative pb-1 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-[#39FF14] after:shadow-[0_0_8px_#39FF14]" data-testid="nav-home">{t("nav.home")}</a>
            <button onClick={() => navigate("/products")} className="hover:text-[#39FF14] transition" data-testid="nav-products">{t("nav.products")}</button>
            <button onClick={() => navigate("/products")} className="hover:text-[#39FF14] transition" data-testid="nav-research">{t("nav.research")}</button>
            <a href="#quality" className="hover:text-[#39FF14] transition" data-testid="nav-quality">{t("nav.quality")}</a>
            <button onClick={() => navigate("/about")} className="hover:text-[#39FF14] transition" data-testid="nav-about">{t("nav.about")}</button>
            <a href="#contact" className="hover:text-[#39FF14] transition" data-testid="nav-contact">{t("nav.contact")}</a>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section id="home" data-testid="hero" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-50" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020409] via-[#020409]/70 to-[#020409]" />
        <div className="absolute inset-0 bg-grid-dots opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/10 blur-[120px] rounded-full" />

        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-16 grid lg:grid-cols-[1fr_1fr_1fr] gap-8 items-start min-h-[640px]">
          {/* LEFT: text */}
          <div className="animate-fade-up">
            <h1 className="font-headline font-black tracking-[-0.04em] leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-3">
              {t("hero.title_1")}
              <br />
              <span className="text-blue-400 glow-blue">{t("hero.title_2")}</span>
              <br />
              {t("hero.title_3")}
            </h1>
            <p className="font-headline text-sm sm:text-base uppercase tracking-[0.15em] font-bold text-white/90 mb-5 mt-3">
              {t("hero.sub")}
            </p>
            <p className="text-zinc-400 text-sm max-w-md mb-7 leading-relaxed">
              {t("hero.body")}
            </p>
            <div className="flex flex-col gap-3 mb-7 max-w-md">
              <form onSubmit={handleSearchSubmit} className="relative" data-testid="hero-search-form">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-300/70 pointer-events-none" />
                <Input
                  data-testid="hero-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("hero.search_placeholder")}
                  className="bg-blue-500/10 border-blue-500/40 focus:border-blue-400 focus-visible:ring-blue-500/40 text-white placeholder:text-blue-200/50 pl-10 pr-24 h-12 font-medium"
                />
                <button
                  type="submit"
                  data-testid="hero-search-btn"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-blue-500 hover:bg-blue-400 text-white text-[10px] font-bold uppercase tracking-wider px-3.5 py-2 rounded-md transition shadow-[0_0_12px_rgba(59,130,246,0.5)]"
                >
                  {t("hero.search_btn")}
                </button>
              </form>
            </div>
            {/* Made in Germany */}
            <div className="inline-flex items-center gap-3 text-xs">
              <div className="flex w-8 h-5 overflow-hidden rounded-sm border border-white/10 flex-col">
                <div className="w-full h-1/3 bg-black" />
                <div className="w-full h-1/3 bg-red-600" />
                <div className="w-full h-1/3 bg-yellow-400" />
              </div>
              <div>
                <div className="font-bold text-white uppercase tracking-[0.15em] text-[11px]">{t("hero.made_in_germany")}</div>
                <div className="text-zinc-500 text-[10px] tracking-wider mt-0.5">{t("hero.made_in_germany_sub")}</div>
              </div>
            </div>
          </div>

          {/* CENTER: Retatrutide pen */}
          <div className="relative flex flex-col items-center justify-start order-3 lg:order-2 lg:pt-2">
            <h3
              data-testid="hero-pens-label"
              className="font-headline text-2xl sm:text-3xl font-black tracking-[0.18em] text-[#39FF14] mb-3"
              style={{ textShadow: "0 0 12px rgba(57,255,20,0.85), 0 0 28px rgba(57,255,20,0.55)" }}
            >
              PENS & SPRAYS
            </h3>
            <div className="relative w-full max-w-[420px] mx-auto">
              <div className="absolute inset-0 bg-blue-500/15 blur-[100px] rounded-full" />
              <button
                type="button"
                onClick={() => navigate("/products?cat=pens")}
                data-testid="hero-pen-click"
                className="relative w-full cursor-pointer hover:scale-[1.02] transition-transform"
                aria-label="View pens"
              >
                <img
                  src={heroPenSrc}
                  alt="Supreme Laboratories Retatrutide Pen"
                  className="w-full rounded-2xl aspect-square object-cover"
                  style={{ filter: "drop-shadow(0 0 50px rgba(59,130,246,0.35))" }}
                />
              </button>
            </div>
          </div>

          {/* RIGHT: bottle + features below */}
          <div className="relative flex flex-col items-center gap-5 order-2 lg:order-3 lg:pt-2">
            <h3
              data-testid="hero-vials-label"
              className="font-headline text-2xl sm:text-3xl font-black tracking-[0.18em] text-[#39FF14]"
              style={{ textShadow: "0 0 12px rgba(57,255,20,0.85), 0 0 28px rgba(57,255,20,0.55)" }}
            >
              VIALS
            </h3>
            <button
              type="button"
              onClick={() => navigate("/products?not=pens")}
              data-testid="hero-bottle-click"
              className="relative w-full cursor-pointer hover:scale-[1.02] transition-transform"
              aria-label="View vials"
            >
              <div className="absolute inset-0 bg-blue-500/20 blur-[80px] rounded-full animate-pulse-slow pointer-events-none" />
              <img
                src={PREMIUM_BOX}
                alt="Supreme Peptide vial"
                className="relative w-full max-w-[420px] mx-auto rounded-2xl aspect-square object-cover"
                style={{ filter: "drop-shadow(0 0 50px rgba(59,130,246,0.35))" }}
              />
            </button>
            {/* Feature bullets — horizontal row beneath the bottle */}
            <div className="hidden lg:grid grid-cols-2 gap-x-4 gap-y-3 w-full max-w-[420px]">
              {HERO_FEATURES.map((f, i) => (
                <div key={i} className="flex items-start gap-2.5" data-testid={`hero-feature-${i}`}>
                  <div className="w-8 h-8 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-center shrink-0">
                    <f.icon className="w-3.5 h-3.5 text-[#39FF14]" strokeWidth={2} />
                  </div>
                  <div className="leading-tight">
                    <div className="text-[10px] font-bold uppercase tracking-[0.13em] text-white">{t(f.titleKey)}</div>
                    <div className="text-[9px] tracking-wider text-zinc-500">{t(f.subKey)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RESEARCH CATEGORIES */}
      <section id="categories" className="py-20 px-6 relative border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#39FF14] font-bold mb-2">{t("cats.eyebrow")}</p>
              <h2 className="font-headline text-3xl md:text-4xl font-black tracking-tighter text-white">
                {t("cats.title")}
              </h2>
            </div>
            <button
              data-testid="view-all-products"
              onClick={() => navigate("/products")}
              className="inline-flex items-center gap-2 border border-white/10 hover:border-[#39FF14]/40 bg-transparent text-[11px] uppercase tracking-[0.2em] font-bold text-white hover:text-[#39FF14] px-5 py-2.5 rounded-md transition">
              {t("cats.view_all")} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {CATEGORIES.map((cat, idx) => {
              const ringColor = cat.accent === "blue"
                ? "hover:border-blue-500/50 hover:shadow-[0_0_25px_rgba(59,130,246,0.2)]"
                : "hover:border-[#39FF14]/50 hover:shadow-[0_0_25px_rgba(57,255,20,0.15)]";
              return (
                <button
                  key={cat.id}
                  onClick={() => navigate(`/products?cat=${cat.id}`)}
                  data-testid={`category-card-${cat.id}`}
                  className={`group relative overflow-hidden bg-black border border-white/5 ${ringColor} rounded-xl aspect-[2/5] flex flex-col transition-all duration-300 text-left animate-fade-up`}
                  style={{ animationDelay: `${idx * 70}ms` }}
                >
                  <div className="relative flex-1 overflow-hidden">
                    <img
                      src={resolveCategoryImage(cat.id, categoryImages)}
                      alt={cat.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="relative bg-black px-3 py-4 min-h-[120px] flex flex-col justify-between">
                    <div
                      className="font-bold uppercase text-white leading-[1.15]"
                      style={{ fontSize: "10.5px", letterSpacing: "0.02em", wordSpacing: "0.05em" }}
                    >
                      {cat.name}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <span className="text-[9.5px] text-zinc-400 leading-tight" style={{ letterSpacing: "0.01em" }}>{cat.sub}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-[#39FF14] shrink-0" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* BUILT ON SCIENCE / STATS */}
      <section id="quality" className="py-20 px-6 border-y border-white/5 bg-[#060912]">
        <div className="max-w-7xl mx-auto">
          <div className="relative bg-[#0A0F1A] border border-white/5 rounded-2xl p-8 md:p-12 overflow-hidden">
            <div className="absolute -left-10 -top-10 w-96 h-96 bg-blue-500/5 blur-3xl rounded-full" />
            <div className="absolute -right-10 -bottom-10 w-96 h-96 bg-[#39FF14]/5 blur-3xl rounded-full" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#39FF14] font-bold mb-3">{t("quality.eyebrow")}</p>
                <h3 className="font-headline text-3xl md:text-4xl font-black tracking-tighter mb-4">
                  {t("quality.title_1")}<br />
                  <span className="text-[#39FF14] glow-green">{t("quality.title_2")}</span>
                </h3>
                <p className="text-zinc-400 text-base leading-relaxed">
                  {t("quality.body")}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {STATS.map((s, i) => (
                  <div key={i} className="text-center md:text-left bg-[#020409]/50 border border-white/5 rounded-xl px-4 py-5" data-testid={`stat-${i}`}>
                    <div className="font-headline text-3xl md:text-4xl font-black text-[#39FF14] glow-green leading-none">{s.v}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-2 leading-tight">{t(s.labelKey)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SHIPPING FEATURES BAR */}
      <section className="border-b border-white/5 bg-[#0A0F1A]">
        <div className="max-w-7xl mx-auto px-6 py-7 grid grid-cols-1 md:grid-cols-4 gap-5">
          {SHIPPING_FEATURES.map((f, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`feature-${i}`}>
              <f.icon className="w-7 h-7 text-[#39FF14]" strokeWidth={1.5} />
              <div className="leading-tight">
                <div className="font-bold uppercase tracking-[0.1em] text-xs text-white">{t(f.titleKey)}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{t(f.descKey)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* RESEARCH USE ONLY DISCLAIMER STRIP */}
      <section data-testid="research-disclaimer" className="relative px-6 py-10 border-t border-white/5 bg-[radial-gradient(ellipse_at_center,rgba(239,68,68,0.08),transparent_70%)]">
        <div className="max-w-5xl mx-auto">
          <div className="relative flex flex-col sm:flex-row items-center gap-4 sm:gap-5 rounded-xl border border-red-500/40 bg-red-500/[0.06] px-5 sm:px-7 py-5 backdrop-blur-sm">
            <div className="w-12 h-12 rounded-full bg-red-500/15 border border-red-500/50 flex items-center justify-center shrink-0 shadow-[0_0_18px_rgba(239,68,68,0.45)]">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-center sm:text-left">
              <p className="font-headline text-base sm:text-lg font-black uppercase tracking-[0.2em] text-red-400 leading-tight">
                Research Use Only
              </p>
              <p className="text-sm text-zinc-300 leading-relaxed mt-1">
                All Supreme Laboratories peptides are intended <span className="text-red-300 font-semibold">strictly for in-vitro laboratory research</span>.
                <span className="hidden sm:inline"> </span>
                <span className="block sm:inline text-red-300 font-bold">Not for human consumption.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOMER REVIEWS */}
      <section className="py-16 px-6 border-t border-white/5 bg-[#020409]" id="reviews">
        <div className="max-w-6xl mx-auto">
          <ReviewsSection />
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contact" className="py-14 px-6 bg-[#020409]">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-[1.3fr_0.9fr_0.9fr_1.3fr_1.3fr] gap-10 mb-10">
          <div>
            <Logo />
            <p className="text-zinc-500 text-sm mt-4 max-w-xs leading-relaxed">
              Leading the future of peptide research with uncompromising quality, innovation, and precision.
            </p>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14] font-bold mb-4">{t("footer.research")}</h4>
            <ul className="space-y-2.5 text-xs text-zinc-400">
              <li><a href="#home" className="hover:text-white transition">{t("nav.home")}</a></li>
              <li><button onClick={() => navigate("/products")} className="hover:text-white transition text-left">{t("nav.products")}</button></li>
              <li><button onClick={() => navigate("/products")} className="hover:text-white transition text-left">{t("nav.research")}</button></li>
              <li><a href="#quality" className="hover:text-white transition">{t("footer.research.quality")}</a></li>
              <li><button onClick={() => navigate("/about")} className="hover:text-white transition text-left" data-testid="footer-about">{t("footer.research.about")}</button></li>
            </ul>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14] font-bold mb-4">{t("footer.account")}</h4>
            <ul className="space-y-2.5 text-xs text-zinc-400">
              <li><a href="/admin" className="hover:text-white transition" data-testid="footer-admin-link">{t("footer.account.account")}</a></li>
              <li><button onClick={() => navigate("/shipping")} className="hover:text-white transition text-left" data-testid="footer-shipping">{t("footer.account.shipping")}</button></li>
              <li><button onClick={() => navigate("/page/terms-and-conditions")} className="hover:text-white transition text-left" data-testid="footer-terms">{t("footer.account.terms")}</button></li>
              <li><button onClick={() => navigate("/page/privacy-policy")} className="hover:text-white transition text-left" data-testid="footer-privacy">{t("footer.account.privacy")}</button></li>
              <li><button onClick={() => navigate("/page/contact-us")} className="hover:text-white transition text-left" data-testid="footer-contact-page">{t("footer.account.contact")}</button></li>
            </ul>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14] font-bold mb-4">{t("footer.contact")}</h4>
            <ul className="space-y-3 text-xs text-zinc-400" data-testid="footer-contact">
              <li className="flex items-start gap-2.5">
                <Mail className="w-3.5 h-3.5 text-[#39FF14] shrink-0 mt-0.5" />
                <a
                  href="mailto:contact@supremelaboratories.com"
                  data-testid="footer-contact-email"
                  className="hover:text-[#39FF14] transition break-all"
                >
                  contact@supremelaboratories.com
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Send className="w-3.5 h-3.5 text-[#39FF14] shrink-0 mt-0.5" />
                <a
                  href={`https://wa.me/${waSupreme}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-contact-whatsapp"
                  className="hover:text-[#39FF14] transition"
                >
                  WhatsApp · {waDisplay}
                </a>
              </li>
            </ul>
            <p className="text-[10px] text-zinc-600 leading-relaxed mt-5 max-w-xs">
              {t("footer.contact.note")}
            </p>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14] font-bold mb-4">{t("footer.follow")}</h4>
            <p className="text-xs text-zinc-500 leading-relaxed mb-4">{t("footer.follow.note")}</p>
            <div className="flex gap-2">
              {[
                { Icon: Instagram, url: "https://www.instagram.com/supremelaboratories?igsh=djI0NmdpdXFzb2Mw", label: "Instagram" },
                { Icon: Send, url: "https://t.me/SupremeLaboratories", label: "Telegram" },
                { Icon: Mail, url: "mailto:contact@supremelaboratories.com", label: "Email" },
              ].map(({ Icon, url, label }, i) => (
                <a
                  key={i}
                  href={url}
                  target={url.startsWith("mailto:") ? undefined : "_blank"}
                  rel={url.startsWith("mailto:") ? undefined : "noopener noreferrer"}
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  className="w-8 h-8 rounded-md bg-white/5 hover:bg-[#39FF14]/15 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-[#39FF14] transition"
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-white/5 pt-6 flex flex-col md:flex-row justify-between gap-3 text-[10px] text-zinc-600 uppercase tracking-wider">
          <p>{t("footer.copyright", { year: new Date().getFullYear() })}</p>
          <p>{t("footer.disclaimer")}</p>
        </div>
      </footer>

      <VerifyDialog open={verifyOpen} onOpenChange={setVerifyOpen} />
      <WhatsAppFab />
      <GymGearFab />
    </div>
  );
}
